import cloneDeep from "lodash/cloneDeep"
import PropTypes from "prop-types"
import React from "react"
import {
  SortableContainer,
  SortableElement,
  SortableHandle
} from "react-sortable-hoc"
import "./sortable-styles.css"

const arrayMoveMutable = (array, fromIndex, toIndex) => {
  const startIndex = fromIndex < 0 ? array.length + fromIndex : fromIndex

  if (startIndex >= 0 && startIndex < array.length) {
    const endIndex = toIndex < 0 ? array.length + toIndex : toIndex

    const [item] = array.splice(fromIndex, 1)
    array.splice(endIndex, 0, item)
  }
}

// https://github.com/sindresorhus/array-move/blob/main/index.js
const arrayMoveImmutable = (array, fromIndex, toIndex) => {
  const newArray = [...array]
  arrayMoveMutable(newArray, fromIndex, toIndex)
  return newArray
}

export const DragSortHandle = (props) => {
  const { children } = props

  const DragHandle = SortableHandle((props) => {
    return props.children()
  })

  return <DragHandle children={children} />
}

export class DragSortElement extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      WrappedComponent: null
    }
  }

  componentDidMount() {
    this.setWrappedComponent()
  }

  setWrappedComponent() {
    const WrappedComponent = SortableElement((props) => {
      return props.childComponent
    })
    this.setState({ WrappedComponent })
  }

  render() {
    const { props } = this
    const { children, index, disabled, collection } = props

    const { WrappedComponent } = this.state
    if (!WrappedComponent) return null

    return (
      <WrappedComponent
        style={disabled ? { transform: "none !important" } : null}
        index={index}
        disabled={disabled}
        collection={collection}
        childComponent={children()}
      />
    )
  }
}

DragSortElement.propTypes = {
  index: PropTypes.number.isRequired,
  disabled: PropTypes.bool,
  collection: PropTypes.number
}

DragSortElement.defaultProps = {
  disabled: false
}

export class DragSortContainer extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      WrappedComponent: null
    }
  }

  componentDidMount() {
    this.setWrappedComponent()
  }

  setWrappedComponent() {
    const WrappedComponent = SortableContainer((props) => {
      return props.childComponent
    })
    this.setState({ WrappedComponent })
  }

  render() {
    const { props } = this

    const {
      direction,
      lock,
      orderKey,
      delay,
      useHandle,
      shouldAlsoCancelStart,
      updateBeforeSortStart,
      onSortStart,
      onSortMove,
      onSortOver,
      onSort,
      children,
      items
    } = props

    const applyOrdering = (unorderedItems, orderKey) => {
      return unorderedItems.map((item, idx) => {
        item[orderKey] = idx + 1
        return item
      })
    }

    const handleDragSort = (
      { oldIndex, newIndex, collection, isKeySorting },
      event
    ) => {
      const updatedCollection = cloneDeep(items)

      // TODO: check if these 2 lines are needed
      updatedCollection[oldIndex][orderKey] = newIndex + 1
      updatedCollection[newIndex][orderKey] = oldIndex + 1

      const sortedCollection = arrayMoveImmutable(
        updatedCollection,
        oldIndex,
        newIndex
      )
      const ordered = applyOrdering(sortedCollection, orderKey)
      onSort(
        { items: ordered, oldIndex, newIndex, collection, isKeySorting },
        event
      )
    }

    const getAxis = (direction) => {
      switch (direction) {
        case "vertical":
          return "y"
        case "horizontal":
          return "x"
        case "grid":
          return "xy"
        default:
          return "x"
      }
    }

    const axis = getAxis(direction)

    const sortableProps = {
      axis,
      lockAxis: lock ? axis : null,
      helperClass: "sortable-card",
      pressDelay: delay,
      useDragHandle: useHandle,
      shouldCancelStart: function (e) {
        // Cancel sorting if the event target is an `input`, `textarea`, `select` or `option`
        const disabledElements = [
          "input",
          "textarea",
          "select",
          "option",
          "button"
        ]

        if (disabledElements.indexOf(e.target.tagName.toLowerCase()) !== -1) {
          return true // Return true to cancel sorting
        }

        if (
          typeof shouldAlsoCancelStart === "function" &&
          shouldAlsoCancelStart(e)
        ) {
          return true
        }

        return false
      },
      updateBeforeSortStart: updateBeforeSortStart,
      onSortStart: onSortStart,
      onSortMove: onSortMove,
      onSortOver: onSortOver,
      onSortEnd: handleDragSort
    }

    const { WrappedComponent } = this.state
    if (!WrappedComponent) return null

    return <WrappedComponent {...sortableProps} childComponent={children()} />
  }
}

DragSortContainer.propTypes = {
  items: PropTypes.array.isRequired,
  direction: PropTypes.string.isRequired,
  lock: PropTypes.bool,
  delay: PropTypes.number,
  orderKey: PropTypes.string,
  useHandle: PropTypes.bool,
  shouldUpdate: PropTypes.bool.isRequired,
  updateBeforeSortStart: PropTypes.func,
  onSortStart: PropTypes.func,
  onSortMove: PropTypes.func,
  onSortOver: PropTypes.func,
  onSort: PropTypes.func.isRequired,
  shouldAlsoCancelStart: PropTypes.func
}

DragSortContainer.defaultProps = {
  items: [],
  direction: "horizontal",
  lock: false,
  delay: 0,
  orderKey: "order",
  useHandle: true,
  shouldUpdate: true,
  updateBeforeSortStart: null,
  onSortStart: null,
  onSortMove: null,
  onSortOver: null,
  onSort: ({ items, oldIndex, newIndex, collection, isKeySorting }, e) =>
    console.log({ items, oldIndex, newIndex, collection, isKeySorting }, e),
  shouldAlsoCancelStart: (e) => false
}
